import type { I18nKey } from '../../i18n/index.ts';

export type EventChipTone = 'ok' | 'warn' | 'bad' | 'neutral';

export interface EventChip {
  readonly tone: EventChipTone;
  readonly labelKey: I18nKey | null;
}

function textOf(details: Readonly<Record<string, unknown>>, key: string): string | null {
  const value = details[key];
  if (typeof value === 'string' && value.length > 0) return value;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function reasonOf(
  details: Readonly<Record<string, unknown>> | null,
  key: 'trip_reason' | 'shadow_trip_reason',
): 'probe' | 'chain' | 'jev' | null {
  if (details === null) return null;
  const value = details[key];
  if (value === 'probe' || value === 'chain' || value === 'jev') return value;
  return null;
}

function actionOf(details: Readonly<Record<string, unknown>> | null): string | null {
  if (details === null) return null;
  const value = details.action_taken;
  return typeof value === 'string' ? value : null;
}

function chip(tone: EventChipTone, labelKey: I18nKey): EventChip {
  return { tone, labelKey };
}

export function eventChip(
  kind: string,
  details: Readonly<Record<string, unknown>> | null,
): EventChip {
  switch (kind) {
    case 'target_probe_recover':
      return chip('ok', 'guaranteed.events.chip.probe_recover');
    case 'shadow_recover':
      return chip('ok', 'guaranteed.events.chip.shadow_recover');
    case 'target_probe_trip': {
      const reason = reasonOf(details, 'trip_reason');
      if (reason === 'chain') return chip('bad', 'guaranteed.events.chip.chain_trip');
      if (reason === 'jev') return chip('bad', 'guaranteed.events.chip.jev_trip');
      return chip('bad', 'guaranteed.events.chip.probe_trip');
    }
    case 'shadow_trip': {
      const reason = reasonOf(details, 'shadow_trip_reason');
      if (reason === 'chain') return chip('warn', 'guaranteed.events.chip.shadow_chain');
      if (reason === 'jev') return chip('warn', 'guaranteed.events.chip.shadow_jev');
      return chip('warn', 'guaranteed.events.chip.shadow_probe');
    }
    case 'guaranteed_shadow_enter':
      return chip('warn', 'guaranteed.events.chip.shadow_enter');
    case 'trip_skipped_immune':
      return chip('neutral', 'guaranteed.events.chip.immune');
    case 'chain_trip_suppressed':
      return chip('neutral', 'guaranteed.events.chip.chain_suppressed');
    case 'judge_error':
      return chip('neutral', 'guaranteed.events.chip.judge_error');
    case 'jev_verdict': {
      const action = actionOf(details);
      if (action === 'trip') return chip('bad', 'guaranteed.events.chip.jev_verdict_trip');
      if (action === 'would_trip') return chip('warn', 'guaranteed.events.chip.jev_verdict_would');
      if (action === 'judge_error') return chip('neutral', 'guaranteed.events.chip.judge_error');
      return chip('neutral', 'guaranteed.events.chip.jev_verdict_record');
    }
    default:
      return { tone: 'neutral', labelKey: null };
  }
}

export type DetailTranslate = (key: I18nKey, params?: Readonly<Record<string, string>>) => string;

const REASON_KEYS: Readonly<Record<string, I18nKey>> = {
  probe: 'guaranteed.leg.trigger.probe',
  chain: 'guaranteed.leg.trigger.chain',
  jev: 'guaranteed.leg.trigger.jev',
  probe_disabled: 'guaranteed.events.reason.probe_disabled',
  immune: 'guaranteed.events.reason.immune',
};

const VERDICT_KEYS: Readonly<Record<string, I18nKey>> = {
  upstream_server: 'guaranteed.events.verdict.upstream_server',
  client: 'guaranteed.events.verdict.client',
  unknown: 'guaranteed.events.verdict.unknown',
};

/** Signatures carry `` field separators and a hash tail; keep the readable head. */
function readableSignature(raw: string): string {
  const head = raw.split('').map((part) => part.trim()).filter((part) => part.length > 0);
  const named = head.filter((part) => !/^[0-9a-f]{12,}$/i.test(part));
  return (named.length > 0 ? named : head).join(' / ');
}

/**
 * One operator-readable line from the event's details. Field names come from
 * the gateway load-event writers (`reason`, `fail`/`ok`, `trip_reason`,
 * `verdict`, `signature`, `status`, `mode`, `last_error_excerpt`, ...);
 * anything not covered is still visible in the JSON tooltip.
 */
export function eventDetailLine(details: Readonly<Record<string, unknown>> | null, t: DetailTranslate): string {
  if (details === null) return '—';
  const parts: string[] = [];
  const reason = textOf(details, 'reason') ?? textOf(details, 'trip_reason') ?? textOf(details, 'shadow_trip_reason');
  if (reason !== null) {
    const key = REASON_KEYS[reason];
    parts.push(key === undefined ? reason : t(key));
  }
  const verdict = textOf(details, 'verdict');
  if (verdict !== null) {
    const key = VERDICT_KEYS[verdict];
    parts.push(key === undefined ? verdict : t(key));
  }
  const mode = textOf(details, 'mode');
  if (mode === 'shadow') parts.push(t('guaranteed.mode.shadow'));
  const fail = textOf(details, 'fail');
  const ok = textOf(details, 'ok');
  if (fail !== null || ok !== null) {
    parts.push(t('guaranteed.events.detail.fail_ok', { fail: fail ?? '0', ok: ok ?? '0' }));
  }
  const hits = textOf(details, 'hit_count') ?? textOf(details, 'hits');
  if (hits !== null) parts.push(t('guaranteed.events.detail.hits', { hits }));
  const orgs = textOf(details, 'org_count') ?? textOf(details, 'orgs');
  if (orgs !== null) parts.push(t('guaranteed.events.detail.orgs', { orgs }));
  const probability = textOf(details, 'p') ?? textOf(details, 'p_upstream_server');
  if (probability !== null) parts.push(`p=${probability}`);
  const http = textOf(details, 'status') ?? textOf(details, 'http_status') ?? textOf(details, 'last_status_code');
  if (http !== null) parts.push(`HTTP ${http}`);
  const rounds = textOf(details, 'rounds') ?? textOf(details, 'consecutive_failed_rounds');
  if (rounds !== null) parts.push(t('guaranteed.events.detail.rounds', { rounds }));
  const signature = textOf(details, 'signature');
  if (signature !== null) parts.push(readableSignature(signature));
  const message = textOf(details, 'message')
    ?? textOf(details, 'excerpt')
    ?? textOf(details, 'last_error_excerpt')
    ?? textOf(details, 'error');
  if (message !== null) {
    if (/^http_\d{3}$/.test(message)) {
      if (http === null) parts.push(`HTTP ${message.slice(5)}`);
    } else {
      parts.push(message);
    }
  }
  return parts.length > 0 ? parts.join(' · ') : '—';
}

export function detailsJson(details: Readonly<Record<string, unknown>> | null): string {
  if (details === null) return 'null';
  try {
    return JSON.stringify(details, null, 2);
  } catch {
    return 'null';
  }
}
